/* eslint-disable */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Checkbox,
  FormGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Tooltip,
  IconButton,
} from '@mui/material';
import Iconify from 'src/components/iconify';
import axiosInstance, { fetcher, patchFetcher } from 'src/utils/axios';
import { useSnackbar } from 'src/components/snackbar';

/* ─── DESIGN TOKENS ──────────────────────────────────── */
const T = {
  radius: '14px',
  radiusSm: '8px',
  radiusXs: '6px',

  optionBg: '#fffbeb',
  optionBorder: '#fcd34d',
  optionAccent: '#d97706',
  optionTag: '#fef3c7',
  optionTagTxt: '#92400e',
  optionStripe: '#f59e0b',

  variantBg: '#f5f3ff',
  variantBorder: '#a5b4fc',
  variantAccent: '#4f46e5',
  variantTag: '#ede9fe',
  variantTagTxt: '#3730a3',
  variantStripe: '#6366f1',

  regularBg: '#f8fafc',
  regularBorder: '#cbd5e1',
  regularAccent: '#475569',
  regularTag: '#f1f5f9',
  regularTagTxt: '#334155',
  regularStripe: '#64748b',

  savedBg: '#dcfce7',
  savedTxt: '#15803d',
  unsavedBg: '#fef9c3',
  unsavedTxt: '#854d0e',
};

const palette = (isOption, isVariant) =>
  isOption ? { bg: T.optionBg, border: T.optionBorder, accent: T.optionAccent, tag: T.optionTag, tagTxt: T.optionTagTxt, stripe: T.optionStripe }
    : isVariant ? { bg: T.variantBg, border: T.variantBorder, accent: T.variantAccent, tag: T.variantTag, tagTxt: T.variantTagTxt, stripe: T.variantStripe }
      : { bg: T.regularBg, border: T.regularBorder, accent: T.regularAccent, tag: T.regularTag, tagTxt: T.regularTagTxt, stripe: T.regularStripe };

/* ─── SMALL HELPERS ──────────────────────────────────── */
const Label = ({ children, icon }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
    {icon && <Iconify icon={icon} width={11} sx={{ color: '#94a3b8' }} />}
    <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
      {children}
    </Typography>
  </Box>
);

const SectionBox = ({ children, sx = {} }) => (
  <Box sx={{
    borderRadius: T.radiusSm,
    border: '1px solid #e2e8f0',
    p: 1.5,
    bgcolor: '#fff',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    ...sx
  }}>
    {children}
  </Box>
);

const EmptyState = ({ icon, text, sub }) => (
  <Box sx={{ py: 3, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75 }}>
    <Box sx={{ width: 40, height: 40, borderRadius: '10px', bgcolor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.5 }}>
      <Iconify icon={icon} width={20} sx={{ color: '#94a3b8' }} />
    </Box>
    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#475569' }}>{text}</Typography>
    {sub && <Typography sx={{ fontSize: '11px', color: '#94a3b8' }}>{sub}</Typography>}
  </Box>
);

/* ─── INLINE ORDER LIST ──────────────────────────────── */
/**
 * Renders below the checkboxes when ≥2 options are selected.
 * Each row has ▲ / ▼ icon buttons — no dialog, no drag.
 *
 * Props:
 *   items    – Array<{id|value, label, cost}> in current order
 *   onChange – (newItems) => void
 */
const InlineOrderList = ({ items, onChange, columns = 1 }) => {
  const move = (fromIdx, toIdx) => {
    if (toIdx < 0 || toIdx >= items.length) return;
    const next = [...items];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    onChange(next);
  };

  return (
    <Box sx={{ mt: 1.25 }}>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 0.75,
        px: 1, py: 0.6, mb: 0.75,
        bgcolor: T.optionTag,
        border: `1px solid ${T.optionBorder}`,
        borderRadius: '7px',
      }}>
        <Iconify icon="mdi:sort" width={12} sx={{ color: T.optionAccent }} />
        <Typography sx={{
          fontSize: '10px', fontWeight: 700,
          color: T.optionTagTxt, letterSpacing: '0.5px', textTransform: 'uppercase',
        }}>
          Execution order
        </Typography>
      </Box>

      {/* One row per selected option */}
      <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, gap: 0.75 }}>
        {items.map((opt, idx) => (
          <Box
            key={opt.id ?? opt.value}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1,
              py: 0.55,
              borderRadius: '8px',
              border: `1.5px solid ${T.optionBorder}`,
              bgcolor: T.optionBg,
              transition: 'box-shadow 0.15s',
              '&:hover': { boxShadow: `0 2px 8px ${T.optionAccent}22` },
            }}
          >
            {/* Position badge */}
            <Box sx={{
              width: 20, height: 20, borderRadius: '5px',
              bgcolor: T.optionAccent,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <Typography sx={{ fontSize: '10px', fontWeight: 800, color: '#fff', lineHeight: 1 }}>
                {idx + 1}
              </Typography>
            </Box>

            {/* Label */}
            <Tooltip title={opt.label} placement="top">            <Typography sx={{
              flex: 1, fontSize: '12px', fontWeight: 600, color: '#1e293b',
              minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {opt.label}
            </Typography></Tooltip>


            {/* Cost chip */}
            {opt.cost && (
              <Chip
                label={`₹${opt.cost}`}
                size="small"
                sx={{
                  height: 16, fontSize: '10px', flexShrink: 0,
                  bgcolor: T.optionTag, color: T.optionTagTxt, borderRadius: '4px',
                }}
              />
            )}

            {/* ▲ ▼ buttons stacked vertically */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', flexShrink: 0 }}>

              <span>
                <IconButton
                  size="small"
                  disabled={idx === 0}
                  onClick={() => move(idx, idx - 1)}
                  sx={{
                    width: 18, height: 18, p: 0, borderRadius: '4px',
                    color: idx === 0 ? '#d1d5db' : T.optionAccent,
                    bgcolor: idx === 0 ? 'transparent' : T.optionTag,
                    '&:hover:not(:disabled)': { bgcolor: '#fde68a' },
                    transition: 'all 0.12s',
                  }}
                >
                  <Iconify icon="mdi:chevron-up" width={14} />
                </IconButton>
              </span>


              <span>
                <IconButton
                  size="small"
                  disabled={idx === items.length - 1}
                  onClick={() => move(idx, idx + 1)}
                  sx={{
                    width: 18, height: 18, p: 0, borderRadius: '4px',
                    color: idx === items.length - 1 ? '#d1d5db' : T.optionAccent,
                    bgcolor: idx === items.length - 1 ? 'transparent' : T.optionTag,
                    '&:hover:not(:disabled)': { bgcolor: '#fde68a' },
                    transition: 'all 0.12s',
                  }}
                >
                  <Iconify icon="mdi:chevron-down" width={14} />
                </IconButton>
              </span>

            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

/* ─── ADD ALLOCABLE VARIANT DIALOG ──────────────────── */
const AddAllocableVariantDialog = ({ open, onClose, rawMaterialId, onAdded }) => {
  const [schema, setSchema] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [attributes, setAttributes] = useState({});
  const [saving, setSaving] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const visibleParams = useMemo(
    () => (schema?.category?.parameters || []).filter(p => p.inventoryAddition && !p.aggregate),
    [schema]
  );

  useEffect(() => {
    if (!open || !rawMaterialId) return;
    setSchema(null);
    setAttributes({});
    setFetching(true);
    axiosInstance
      .get(`products-app/product-variant/get-or-create/${rawMaterialId}/`)
      .then(res => {
        setSchema(res.data);
        setAttributes(res.data?.attributes || {});
      })
      .catch(err => {
        console.error(err);
        enqueueSnackbar('Failed to load variant schema', { variant: 'error' });
      })
      .finally(() => setFetching(false));
  }, [open, rawMaterialId]);

  const handleClose = () => { setSchema(null); setAttributes({}); onClose(); };
  const setAttr = (key, val) => setAttributes(p => ({ ...p, [key]: val }));

  const handleSubmit = async () => {
    const missing = visibleParams.filter(p => p.required && !String(attributes[p.key] ?? '').trim());
    if (missing.length > 0) {
      enqueueSnackbar(`Please fill: ${missing.map(p => p.key).join(', ')}`, { variant: 'warning' });
      return;
    }

    // Only send inventory-addition keys — mirrors backend's allowed key set
    const payloadAttributes = {};
    visibleParams.forEach(p => {
      payloadAttributes[p.key] = attributes[p.key] ?? '';
    });

    setSaving(true);

    try {
      const res = await axiosInstance.post(`products-app/product-variant/get-or-create/${rawMaterialId}/`, { material_id: rawMaterialId, attributes: payloadAttributes });
      enqueueSnackbar('Allocable variant saved', { variant: 'success' });
      if (onAdded) onAdded(res.data);
      handleClose();
    } catch (err) {
      console.error(err);
      enqueueSnackbar('Failed to save variant', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const renderField = (param) => {
    const { key, type, options = [], unit, required } = param;
    const label = `${key}${unit ? ` (${unit})` : ''}${required ? ' *' : ''}`;
    const val = attributes[key] ?? '';

    if (type === 'radio' && options.length > 0) {
      return (
        <Box key={key}>
          <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#64748b', mb: 0.75 }}>{label}</Typography>
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
            {options.map(opt => {
              const selected = val === opt;
              return (
                <Box key={opt} onClick={() => setAttr(key, opt)} sx={{
                  px: 1.5, py: 0.5, borderRadius: '50px',
                  border: `1.5px solid ${selected ? T.variantAccent : '#e2e8f0'}`,
                  bgcolor: selected ? T.variantTag : '#fff',
                  color: selected ? T.variantAccent : '#64748b',
                  fontSize: '12px', fontWeight: selected ? 600 : 400,
                  cursor: 'pointer', transition: 'all 0.15s', userSelect: 'none',
                  '&:hover': { borderColor: T.variantAccent, bgcolor: T.variantTag },
                }}>
                  {opt}
                </Box>
              );
            })}
          </Box>
        </Box>
      );
    }

    return (
      <TextField key={key} label={label} size="small" fullWidth
        type={type === 'number' ? 'number' : 'text'}
        value={val} onChange={e => setAttr(key, e.target.value)}
        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
      />
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: '16px', boxShadow: '0 24px 64px rgba(0,0,0,0.15)' } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: '12px', bgcolor: T.variantAccent, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Iconify icon="mdi:package-variant-plus" width={20} sx={{ color: '#fff' }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '15px', color: '#0f172a' }}>Add Allocable Variant</Typography>
            <Typography sx={{ fontSize: '11px', color: '#94a3b8', mt: 0.1 }}>
              Material ID: <span style={{ fontFamily: 'monospace', color: T.variantAccent }}>{rawMaterialId}</span>
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: '12px !important' }}>
        {fetching ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 5, gap: 1.5 }}>
            <CircularProgress size={32} sx={{ color: T.variantAccent }} />
            <Typography sx={{ fontSize: '12px', color: '#94a3b8' }}>Loading variant schema…</Typography>
          </Box>
        ) : !schema ? (
          <EmptyState icon="mdi:alert-circle-outline" text="Failed to load schema" sub="Please try again" />
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ p: 1.5, borderRadius: '10px', bgcolor: T.variantTag, border: `1px solid ${T.variantBorder}`, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              {[
                { label: 'Name', value: schema.name },
                { label: 'SKU', value: schema.product_sku, mono: true },
                schema.category?.name && { label: 'Category', value: schema.category.name },
              ].filter(Boolean).map(({ label, value, mono }) => (
                <Box key={label}>
                  <Typography sx={{ fontSize: '10px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.6px', mb: 0.25 }}>{label}</Typography>
                  <Typography sx={{ fontSize: '12px', fontWeight: 600, color: T.variantTagTxt, fontFamily: mono ? 'monospace' : 'inherit' }}>{value}</Typography>
                </Box>
              ))}
            </Box>
            {visibleParams.length === 0
              ? <EmptyState icon="mdi:cog-outline" text="No configurable fields" sub="This variant has no additional parameters" />
              : visibleParams.map(param => renderField(param))
            }
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={handleClose} variant="outlined" size="small"
          sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" size="small"
          disabled={saving || fetching || !schema}
          startIcon={saving ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <Iconify icon="mdi:check" width={15} />}
          sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, bgcolor: T.variantAccent, boxShadow: 'none', '&:hover': { bgcolor: '#4338ca', boxShadow: 'none' } }}>
          {saving ? 'Saving…' : 'Save Variant'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ─── TRANSFORMATION SECTION ────────────────────────── */
const TransformationSection = ({ nodeId, transformations, onChange }) => {
  const nodeTransforms = transformations[nodeId] || [];
  if (!nodeTransforms.length) return null;

  const handleTransformChange = (transformIdx, field, value) => {
    const updated = [...nodeTransforms];
    updated[transformIdx] = { ...updated[transformIdx], [field]: value };
    onChange(nodeId, updated);
  };

  return (
    <SectionBox>
      <Label icon="mdi:math-integral-box">Attribute Transformations</Label>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {nodeTransforms.map((transform, idx) => (
          <Box
            key={idx}
            sx={{
              p: 1.25,
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              bgcolor: '#f8fafc',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            {/* Product.Attribute label */}
            <Box sx={{ flex: '0 0 auto' }}>
              <Tooltip title={`${transform.productId}.${transform.attribute}`} placement="top">
                <Typography
                  sx={{
                    fontSize: '11px',
                    fontWeight: 700,
                    color: '#1e293b',
                    fontFamily: 'monospace',
                    px: 0.75,
                    py: 0.5,
                    bgcolor: '#e2e8f0',
                    borderRadius: '6px',
                    maxWidth: '150px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {transform.attribute}
                </Typography>
              </Tooltip>
            </Box>

            {/* Operator dropdown */}
            <Box sx={{ flex: '0 0 auto' }}>
              <TextField
                select
                size="small"
                disabled={true} // Operator editing is currently disabled
                value={transform.operator || '/'}
                onChange={(e) => handleTransformChange(idx, 'operator', e.target.value)}
                sx={{
                  width: '60px',
                  '& .MuiOutlinedInput-root': { borderRadius: '6px', fontSize: '12px' },
                  '& .MuiSelect-icon': { fontSize: '18px' },
                }}
              >
                {['/', '*', '+', '-'].map((op) => (
                  <option key={op} value={op}>
                    {op}
                  </option>
                ))}
              </TextField>
            </Box>

            {/* User value input */}
            <Box sx={{ flex: '1 1 100px', minWidth: '100px' }}>
              <TextField
                size="small"
                type="number"
                placeholder="Value"
                value={transform.userValue || ''}
                onChange={(e) => handleTransformChange(idx, 'userValue', e.target.value)}
                sx={{
                  width: '100%',
                  '& .MuiOutlinedInput-root': { borderRadius: '6px', fontSize: '12px' },
                }}
              />
            </Box>

            {/* Display current formula */}
            <Box sx={{ flex: '0 0 auto' }}>
              <Typography sx={{ fontSize: '10px', color: '#64748b', fontFamily: 'monospace' }}>
                = {transform.attribute} {transform.operator} {transform.userValue || '?'}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </SectionBox>
  );
};

/* ─── NODE CARD ─────────────────────────────────────── */
const NodeCard = ({
  node, isLoading, nodeHasData, isOptionNode, hasRawMaterial,
  nodeOptions, selectedOptions, onOptionChange,
  variantOptions, allocableVariantOptions,
  selectedVariants, selectedAllocableVariants,
  onVariantChange, onAllocableVariantChange,
  nodeStates, getCombinedVariantIds,
  onAllocableVariantAdded,
  orderedOptions,
  onOrderChange,
  transformations,
  onTransformationsChange,
}) => {
  const nodeId = node.id;
  const p = palette(isOptionNode, hasRawMaterial);
  const isUnsaved = !node.data?.updated;
  const hasVariants = getCombinedVariantIds(nodeId).length > 0;
  const hasOptions = (selectedOptions[nodeId] || []).length > 0;
  const typeLabel = isOptionNode ? 'Option' : hasRawMaterial ? 'Variant' : 'Regular';
  const typeIcon = isOptionNode ? 'mdi:format-list-checks' : hasRawMaterial ? 'mdi:package-variant' : 'mdi:cube-outline';

  const optionCount = nodeOptions[nodeId]?.options?.length || 0;
  const optionColumns = optionCount > 6 ? 3 : optionCount > 3 ? 2 : 1;

  const [addDialogRmId, setAddDialogRmId] = useState(null);

  // Merge orderedOptions with current selection, appending any newly checked items at the end
  const currentOrdered = useMemo(() => {
    const ordered = orderedOptions[nodeId] || [];
    const selIds = selectedOptions[nodeId] || [];
    const cfg = nodeOptions[nodeId] || { options: [] };

    if (ordered.length > 0) {
      const base = ordered.filter(o => selIds.includes(o.id ?? o.value));
      const inBase = new Set(base.map(o => o.id ?? o.value));
      const newItems = selIds
        .filter(id => !inBase.has(id))
        .map(id => cfg.options.find(o => (o.id ?? o.value) === id))
        .filter(Boolean);
      return [...base, ...newItems];
    }
    return cfg.options.filter(o => selIds.includes(o.id ?? o.value));
  }, [nodeId, orderedOptions, selectedOptions, nodeOptions]);

  const showOrderList = (selectedOptions[nodeId] || []).length >= 2;

  const handleCheckboxChange = (optId, checked) => {
    onOptionChange(nodeId, optId, checked, false);
  };

  const handleInlineReorder = (newItems) => {
    const newIds = newItems.map(o => o.id ?? o.value);
    onOrderChange(nodeId, newIds, newItems);
  };

  return (
    <Box sx={{
      flex: '1 1 320px',
      maxWidth: 400,
      minWidth: 300,
      borderRadius: T.radius,
      border: `1.5px solid ${p.border}`,
      bgcolor: p.bg,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      transition: 'box-shadow 0.2s, transform 0.2s',
      '&:hover': { boxShadow: '0 10px 32px rgba(0,0,0,0.1)', transform: 'translateY(-2px)' },
    }}>

      {/* ── Accent stripe ── */}
      <Box sx={{ height: 3, bgcolor: p.stripe, flexShrink: 0 }} />

      {/* ── Header ── */}
      <Box sx={{ px: 2, pt: 1.75, pb: 1.5, borderBottom: `1px solid ${p.border}`, display: 'flex', alignItems: 'flex-start', gap: 1.5, flexShrink: 0 }}>
        <Box sx={{
          width: 36, height: 36, borderRadius: '10px',
          bgcolor: p.accent, display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0, mt: 0.25, boxShadow: `0 2px 8px ${p.accent}40`,
        }}>
          <Iconify icon={typeIcon} width={17} sx={{ color: '#fff' }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Tooltip title={node.data?.label || 'Unnamed Node'} placement="top">
            <Typography sx={{
              fontWeight: 700, fontSize: '13.5px', lineHeight: 1.3,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#0f172a',
            }}>
              {node.data?.label || 'Unnamed Node'}
            </Typography>
          </Tooltip>
          <Box sx={{ display: 'flex', gap: 0.6, mt: 0.6, flexWrap: 'wrap', alignItems: 'center' }}>
            <Chip label={typeLabel} size="small" sx={{ height: 18, fontSize: '10px', fontWeight: 700, bgcolor: p.tag, color: p.tagTxt, borderRadius: '5px' }} />
            {isUnsaved
              ? <Chip label="Unsaved" size="small" sx={{ height: 18, fontSize: '10px', bgcolor: T.unsavedBg, color: T.unsavedTxt, borderRadius: '5px' }} />
              : <Chip
                icon={<Iconify icon="mdi:check-circle" width={10} sx={{ color: T.savedTxt, ml: '4px !important' }} />}
                label="Saved" size="small"
                sx={{ height: 18, fontSize: '10px', bgcolor: T.savedBg, color: T.savedTxt, borderRadius: '5px' }}
              />
            }
          </Box>
        </Box>
        {isLoading && <CircularProgress size={16} sx={{ flexShrink: 0, mt: 0.75, color: p.accent }} />}
      </Box>

      {/* ── Body ── */}
      <Box sx={{ px: 2, py: 1.75, flex: 1, display: 'flex', flexDirection: 'column', gap: 1.25 }}>

        {isLoading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: 1 }}>
            <CircularProgress size={26} sx={{ color: p.accent }} />
            <Typography sx={{ fontSize: '11px', color: '#94a3b8' }}>Loading node data…</Typography>
          </Box>

        ) : !nodeHasData ? (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <EmptyState icon="mdi:alert-circle-outline" text="Unable to load data" sub="Check your connection and try again" />
          </Box>

        ) : (
          <>
            {/* ── Option node ── */}
            {isOptionNode && nodeOptions[nodeId]?.options?.length > 0 && (
              <SectionBox>
                <Label icon="mdi:format-list-checks">Select options</Label>
                <FormControl component="fieldset" fullWidth>
                  {nodeOptions[nodeId]?.selectionType === 'radio' ? (
                    <RadioGroup
                      value={selectedOptions[nodeId]?.[0] || ''}
                      onChange={(e) => onOptionChange(nodeId, e.target.value, true, true)}
                    >
                      <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${optionColumns}, minmax(0, 1fr))`, gap: 0.75 }}>
                        {nodeOptions[nodeId].options.map((opt) => (
                          <FormControlLabel
                            key={opt.id || opt.value}
                            value={opt.id || opt.value}
                            control={<Radio size="small" sx={{ color: p.accent, '&.Mui-checked': { color: p.accent }, py: 0.5 }} />}
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Typography sx={{ fontSize: '12px', color: '#334155' }}>{opt.label}</Typography>
                                {opt.cost && <Chip label={`₹${opt.cost}`} size="small" sx={{ height: 18, fontSize: '10px', bgcolor: p.tag, color: p.tagTxt, borderRadius: '5px' }} />}
                              </Box>
                            }
                            sx={{ width: '100%' }}
                          />
                        ))}
                      </Box>
                    </RadioGroup>
                  ) : (
                    /* ── CHECKBOX + inline order list ── */
                    <Box>
                      <FormGroup sx={{ display: 'grid', gridTemplateColumns: `repeat(${optionColumns}, minmax(0, 1fr))`, gap: 0.75 }}>
                        {nodeOptions[nodeId].options.map((opt) => {
                          const optId = opt.id || opt.value;
                          const checked = (selectedOptions[nodeId] || []).includes(optId);
                          return (
                            <FormControlLabel
                              key={optId}
                              control={
                                <Checkbox
                                  size="small"
                                  checked={checked}
                                  onChange={(e) => handleCheckboxChange(optId, e.target.checked)}
                                  sx={{ color: p.accent, '&.Mui-checked': { color: p.accent }, py: 0.5 }}
                                />
                              }
                              label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>

                                  <Typography sx={{ fontSize: '12px', color: '#334155' }}>{opt.label}</Typography>

                                  {opt.cost && <Chip label={`₹${opt.cost}`} size="small" sx={{ height: 18, fontSize: '10px', bgcolor: p.tag, color: p.tagTxt, borderRadius: '5px' }} />}
                                </Box>
                              }
                              sx={{ width: '100%' }}
                            />
                          );
                        })}
                      </FormGroup>

                      {/* Inline order list — appears automatically when ≥2 selected */}
                      {showOrderList && (
                        <InlineOrderList
                          items={currentOrdered}
                          onChange={handleInlineReorder}
                          columns={optionColumns}
                        />
                      )}
                    </Box>
                  )}
                </FormControl>
              </SectionBox>
            )}

            {/* ── Variant node ── */}
            {hasRawMaterial && (nodeStates[nodeId]?.rawMaterials || []).map((rmId, idx) => {
              const hasAllocableOptions = (allocableVariantOptions[rmId] || []).length > 0;
              const hasVariantOptions = (variantOptions[rmId] || []).length > 0;
              return (
                <SectionBox key={rmId}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.25 }}>
                    <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: p.accent }} />
                    <Typography sx={{ fontSize: '10px', fontWeight: 700, color: p.tagTxt, textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                      Material {idx + 1}
                    </Typography>
                    <Box sx={{ flex: 1, height: '1px', bgcolor: p.border }} />
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                    {hasVariantOptions && (
                      <Box>
                        <Label icon="mdi:tag-outline">Regular Variant</Label>
                        <Autocomplete
                          options={variantOptions[rmId] || []}
                          isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
                          getOptionLabel={(opt) => opt?.name ? `${opt.name} (₹${opt.calculated_cost ?? '?'})` : ''}
                          value={selectedVariants[nodeId]?.[rmId]?.[0] || null}
                          onChange={(e, val) => onVariantChange(nodeId, rmId, val)}
                          renderInput={(params) => (
                            <Tooltip title={selectedVariants[nodeId]?.[rmId]?.[0]?.name || ''} placement="top">
                              <TextField {...params} size="small" placeholder="Search & select variant…"
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: T.radiusSm, bgcolor: '#fff', fontSize: '12px' } }}
                              />
                            </Tooltip>
                          )}
                        />
                      </Box>
                    )}
                    {hasAllocableOptions && (
                      <>
                        {hasVariantOptions && <Divider sx={{ my: 0.25 }} />}
                        <Box>
                          <Label icon="mdi:layers-outline">Allocable Variants</Label>
                          <Autocomplete
                            multiple
                            options={allocableVariantOptions[rmId] || []}
                            isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
                            getOptionLabel={(opt) => opt?.name ? `${opt.name} (₹${opt.calculated_cost?.toFixed(2) ?? '—'})` : ''}
                            value={selectedAllocableVariants[nodeId]?.[rmId] || []}
                            onChange={(e, val) => onAllocableVariantChange(nodeId, rmId, val)}
                            renderInput={(params) => (
                              <Tooltip title={(selectedAllocableVariants[nodeId]?.[rmId] || []).map(v => v.name).join(', ') || ''} placement="top">
                                <TextField {...params} size="small" placeholder="Search & select…"
                                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: T.radiusSm, bgcolor: '#fff', fontSize: '12px' } }}
                                />
                              </Tooltip>
                            )}
                            renderTags={(value, getTagProps) =>
                              value.map((opt, i) => (
                                <Chip key={opt.id} label={opt.name} size="small" {...getTagProps({ index: i })}
                                  sx={{ bgcolor: p.tag, color: p.tagTxt, fontSize: '10px', borderRadius: '5px', height: 20 }}
                                />
                              ))
                            }
                          />
                          <Button
                            size="small" variant="outlined"
                            startIcon={<Iconify icon="mdi:plus" width={13} />}
                            onClick={() => setAddDialogRmId(rmId)}
                            sx={{
                              mt: 1, borderRadius: T.radiusSm, textTransform: 'none',
                              fontSize: '11px', fontWeight: 600,
                              color: T.variantAccent, borderColor: T.variantBorder, bgcolor: T.variantTag,
                              '&:hover': { bgcolor: '#ddd6fe', borderColor: T.variantAccent },
                              py: 0.4, px: 1.25,
                            }}
                          >
                            Add New Allocable Variant
                          </Button>
                        </Box>
                      </>
                    )}
                  </Box>
                </SectionBox>
              );
            })}

            {/* ── Transformations ── */}
            <TransformationSection
              nodeId={nodeId}
              transformations={transformations}
              onChange={onTransformationsChange}
            />

            {/* ── Regular node ── */}
            {!isOptionNode && !hasRawMaterial && nodeHasData && (
              <Box sx={{
                flex: 1, display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                borderRadius: T.radiusSm, border: '1.5px dashed #e2e8f0', py: 3, gap: 0.75,
              }}>
                <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Iconify icon="mdi:check-circle-outline" width={20} sx={{ color: '#94a3b8' }} />
                </Box>
                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#94a3b8' }}>No Action Required</Typography>
                <Typography sx={{ fontSize: '10px', color: '#cbd5e1' }}>This node is automatically handled</Typography>
              </Box>
            )}

            {/* ── Ready banner ── */}
            {(hasVariants || hasOptions) && (
              <Box sx={{
                mt: 'auto', borderRadius: T.radiusSm,
                bgcolor: '#f0fdf4', border: '1px solid #bbf7d0',
                px: 1.5, py: 1,
                display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
              }}>
                <Iconify icon="mdi:check-circle" width={14} sx={{ color: '#16a34a' }} />
                <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#15803d' }}>Ready to save</Typography>
                {hasVariants && (
                  <Chip label={`${getCombinedVariantIds(nodeId).length} variant${getCombinedVariantIds(nodeId).length !== 1 ? 's' : ''}`}
                    size="small" sx={{ height: 18, fontSize: '10px', bgcolor: T.variantTag, color: T.variantTagTxt, borderRadius: '5px', ml: 'auto' }} />
                )}
                {hasOptions && (
                  <Chip label={`${(selectedOptions[nodeId] || []).length} option${(selectedOptions[nodeId] || []).length !== 1 ? 's' : ''}`}
                    size="small" sx={{ height: 18, fontSize: '10px', bgcolor: T.optionTag, color: T.optionTagTxt, borderRadius: '5px' }} />
                )}
              </Box>
            )}
          </>
        )}
      </Box>

      {/* ── Dialogs ── */}
      {addDialogRmId && (
        <AddAllocableVariantDialog
          open={Boolean(addDialogRmId)}
          rawMaterialId={addDialogRmId}
          onClose={() => setAddDialogRmId(null)}
          onAdded={(postResponse) => {
            if (onAllocableVariantAdded) onAllocableVariantAdded(addDialogRmId, postResponse, nodeId);
          }}
        />
      )}
    </Box>
  );
};

/* ─── MAIN COMPONENT ─────────────────────────────────── */
const SmartView = ({
  nodes, rfNodes, open, onClose, onNodeUpdate,
  allVariantIds, setAllVariantIds,
  setissaveworkflowready, setShowVariantDialog,
  finalQuantity, issaveworkflowready, setShowSaveDialog,
  hasWorkflowVariantId = false,
  onRegisterSaveAll = null,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const [nodeStates, setNodeStates] = useState({});
  const [loading, setLoading] = useState({});
  const [savingAll, setSavingAll] = useState(false);
  const [nodeDataCache, setNodeDataCache] = useState({});
  const [fetchedNodeData, setFetchedNodeData] = useState({});
  const [variantOptions, setVariantOptions] = useState({});
  const [allocableVariantOptions, setAllocableVariantOptions] = useState({});
  const [selectedVariants, setSelectedVariants] = useState({});
  const [selectedAllocableVariants, setSelectedAllocableVariants] = useState({});
  const [nodeOptions, setNodeOptions] = useState({});
  const [selectedOptions, setSelectedOptions] = useState({});
  const [orderedOptions, setOrderedOptions] = useState({});
  const [transformations, setTransformations] = useState({});

  const { optionNodes, variantNodes, regularNodes } = useMemo(() => {
    const option = [], variant = [], regular = [];
    rfNodes.forEach(node => {
      const isOpt = node.type === 'option' || node.data?.type === 'option';
      const hasRM = node.data?.input?.some(i => i.is_material) || node.data?.hasRawMaterial;
      if (isOpt) option.push(node); else if (hasRM) variant.push(node); else regular.push(node);
    });
    return { optionNodes: option, variantNodes: variant, regularNodes: regular };
  }, [rfNodes]);

  useEffect(() => {
    rfNodes.forEach(node => {
      const nodeId = node.id;
      const isOpt = node.type === 'option' || node.data?.type === 'option';
      if (!isOpt) return;
      const rawSel = node.data?.selected_options || [];
      if (!Array.isArray(rawSel) || rawSel.length === 0) return;
      const ids = rawSel.map(o => (typeof o === 'object' && o !== null ? (o.id || o.option_id || o.value || o.label) : o)).filter(Boolean);
      if (ids.length === 0) return;

      setSelectedOptions(prev => {
        if (prev[nodeId] && prev[nodeId].length > 0) return prev;
        return { ...prev, [nodeId]: ids };
      });
    });
  }, [rfNodes]);

  const fetchedNodeIds = React.useRef(new Set());
  useEffect(() => {
    if (open) rfNodes.forEach(node => { if (!fetchedNodeIds.current.has(node.id)) fetchNodeData(node); });
  }, [open, rfNodes]);

  const fetchNodeData = async (node) => {
    const nodeId = node.id, backendId = node.data?.backendId;
    if (!backendId) return;
    setLoading(p => ({ ...p, [nodeId]: true }));
    try {
      const res = await fetcher([`/new/workflow-app/node/${backendId}/`]);
      console.log('Fetched data for node', nodeId, res);
      fetchedNodeIds.current.add(nodeId);
      setNodeDataCache(p => ({ ...p, [backendId]: res }));
      setFetchedNodeData(p => ({ ...p, [nodeId]: res }));
      setNodeStates(p => ({ ...p, [nodeId]: { name: res.name, rawMaterials: res.input_foreign?.[0]?.value?.product_ids || [] } }));
      const isOpt = node.type === 'option' || res.type === 'option' || res.options_node;
      if (isOpt) {
        const cfg = res.options_node || node.data?.options_config || node.data?.options || {};
        const rawSel = (res.selected_options && res.selected_options.length > 0)
          ? res.selected_options
          : (node.data?.selected_options || []);
        const ids = rawSel.map(o => (typeof o === 'object' && o !== null ? (o.id || o.option_id || o.value || o.label) : o)).filter(Boolean);
        setNodeOptions(p => ({ ...p, [nodeId]: { selectionType: cfg.selectionType || 'radio', options: cfg.options || [] } }));
        setSelectedOptions(p => ({ ...p, [nodeId]: ids }));

        const optionsList = cfg.options || [];
        const orderedObjs = ids
          .map(id => optionsList.find(o => String(o.id ?? o.value) === String(id)))
          .filter(Boolean);
        if (orderedObjs.length > 0) {
          setOrderedOptions(p => ({ ...p, [nodeId]: orderedObjs }));
        }
      }

      // Extract transformations from output_foreign
      const outputTransformations = res.output_foreign?.[0]?.transformations || [];
      if (outputTransformations.length > 0) {
        setTransformations(p => ({ ...p, [nodeId]: outputTransformations }));
      }

      const productIds = res.input_foreign?.[0]?.value?.product_ids || node.data?.rawMaterials || node.data?.input?.[0]?.value?.product_ids || [];

      const rawSavedVarIds = [
        ...(res.input_foreign?.[0]?.value?.product_variant_ids || []),
        ...(res.input_foreign?.[0]?.product_variant_ids || []),
        ...(node.data?.product_variant_ids || []),
        ...(Array.isArray(node.data?.product_variant_data) ? node.data.product_variant_data.map(v => v.id) : [])
      ].filter(Boolean);
      const savedVariantIds = [...new Set(rawSavedVarIds.map(String))];

      const savedVariantData = [
        ...(res.input_foreign?.[0]?.product_variant_data || []),
        ...(res.input_foreign?.[0]?.value?.product_variant_data || []),
        ...(Array.isArray(node.data?.product_variant_data) ? node.data.product_variant_data : [])
      ];

      const savedAllocableData = [
        ...(res.input_foreign?.[0]?.allocable_product_variant_data || []),
        ...(res.input_foreign?.[0]?.value?.allocable_product_variant_data || []),
        ...(Array.isArray(node.data?.allocable_product_variant_data) ? node.data.allocable_product_variant_data : [])
      ];

      const nodeVarObj = {};
      const nodeAllocObj = {};

      await Promise.all(
        productIds.map(async (id) => {
          const variantsList = await fetchVariants(id);
          const allocList = await fetchAllocable(id);

          // Restore saved regular variants for this raw material
          let selectedVarForRm = [];
          if (Array.isArray(savedVariantData) && savedVariantData.length > 0) {
            selectedVarForRm = savedVariantData.filter(v =>
              v.product_id === id ||
              v.product_id?.toString() === id.toString() ||
              v.raw_material_id === id ||
              v.raw_material_id?.toString() === id.toString() ||
              (!v.product_id && !v.raw_material_id)
            );
          }
          if (selectedVarForRm.length === 0 && savedVariantIds.length > 0 && Array.isArray(variantsList)) {
            selectedVarForRm = variantsList.filter(v =>
              savedVariantIds.some(svId => String(svId) === String(v.id))
            );
          }

          // Ensure restored variants use matching option objects from fetched variantsList
          if (Array.isArray(variantsList) && variantsList.length > 0 && selectedVarForRm.length > 0) {
            selectedVarForRm = selectedVarForRm.map(sv =>
              variantsList.find(v => String(v.id) === String(sv.id)) || sv
            );
          }

          // Restore saved allocable variants for this raw material
          let selectedAllocForRm = [];
          if (Array.isArray(savedAllocableData) && savedAllocableData.length > 0) {
            selectedAllocForRm = savedAllocableData.filter(v =>
              v.product_id === id ||
              v.product_id?.toString() === id.toString() ||
              v.raw_material_id === id ||
              v.raw_material_id?.toString() === id.toString() ||
              (!v.product_id && !v.raw_material_id)
            );
          }
          if (selectedAllocForRm.length === 0 && savedVariantIds.length > 0 && Array.isArray(allocList)) {
            selectedAllocForRm = allocList.filter(v =>
              savedVariantIds.some(svId => String(svId) === String(v.id))
            );
          }

          if (Array.isArray(allocList) && allocList.length > 0 && selectedAllocForRm.length > 0) {
            selectedAllocForRm = selectedAllocForRm.map(sv =>
              allocList.find(v => String(v.id) === String(sv.id)) || sv
            );
          }

          nodeVarObj[id] = selectedVarForRm;
          nodeAllocObj[id] = selectedAllocForRm;
        })
      );

      setSelectedVariants(p => ({ ...p, [nodeId]: nodeVarObj }));
      setSelectedAllocableVariants(p => ({ ...p, [nodeId]: nodeAllocObj }));
    } catch (err) {
      console.error(err);
      enqueueSnackbar(`Failed to load ${node.data?.label}`, { variant: 'error' });
    } finally {
      setLoading(p => ({ ...p, [nodeId]: false }));
    }
  };

  const fetchVariants = async (id) => {
    try {
      const r = await fetcher([`/new/workflow-app/products/variants/list/${id}/`]);
      const list = r?.results || r || [];
      setVariantOptions(p => ({ ...p, [id]: list }));
      return list;
    } catch {
      setVariantOptions(p => ({ ...p, [id]: [] }));
      return [];
    }
  };
  const fetchAllocable = async (id) => {
    try {
      const r = await fetcher([`/new/workflow-app/products/variants/list/is/allocable/${id}/`]);
      const list = Array.isArray(r) ? r : [];
      setAllocableVariantOptions(p => ({ ...p, [id]: list }));
      return list;
    } catch {
      setAllocableVariantOptions(p => ({ ...p, [id]: [] }));
      return [];
    }
  };

  const handleAllocableVariantAdded = async (rmId, postResponse, sourceNodeId) => {
    const newVariantId = postResponse?.variant?.id;
    try {
      const r = await fetcher([`/new/workflow-app/products/variants/list/is/allocable/${rmId}/`]);
      const freshList = Array.isArray(r) ? r : [];
      setAllocableVariantOptions(p => ({ ...p, [rmId]: freshList }));
      if (newVariantId && sourceNodeId) {
        const newVariantObj = freshList.find(v => v.id === newVariantId);
        if (newVariantObj) {
          setSelectedAllocableVariants(p => ({
            ...p,
            [sourceNodeId]: { ...(p[sourceNodeId] || {}), [rmId]: [...(p[sourceNodeId]?.[rmId] || []), newVariantObj] },
          }));
        }
      }
    } catch (err) {
      console.error('Failed to refresh allocable variants:', err);
      if (postResponse?.variant) setAllocableVariantOptions(p => ({ ...p, [rmId]: [...(p[rmId] || []), postResponse.variant] }));
    }
  };

  const handleVariantChange = (nodeId, rmId, val) => {
    const newValObj = val ? [val] : [];
    setSelectedVariants(p => {
      const updatedNodeVars = { ...(p[nodeId] || {}), [rmId]: newValObj };
      const node = rfNodes.find(n => n.id === nodeId);
      if (node && onNodeUpdate) {
        const allReg = Object.values(updatedNodeVars).flat().filter(Boolean);
        const allAlloc = Object.values(selectedAllocableVariants[nodeId] || {}).flat().filter(Boolean);
        const allIds = [...new Set([...allReg.map(v => v.id), ...allAlloc.map(v => v.id)])];
        onNodeUpdate(node, {
          ...node.data,
          product_variant_ids: allIds,
          product_variant_data: allReg,
          allocable_product_variant_data: allAlloc
        });
      }
      return { ...p, [nodeId]: updatedNodeVars };
    });
  };

  const handleAllocableVariantChange = (nodeId, rmId, val) => {
    const newValObjs = val || [];
    setSelectedAllocableVariants(p => {
      const updatedNodeAlloc = { ...(p[nodeId] || {}), [rmId]: newValObjs };
      const node = rfNodes.find(n => n.id === nodeId);
      if (node && onNodeUpdate) {
        const allReg = Object.values(selectedVariants[nodeId] || {}).flat().filter(Boolean);
        const allAlloc = Object.values(updatedNodeAlloc).flat().filter(Boolean);
        const allIds = [...new Set([...allReg.map(v => v.id), ...allAlloc.map(v => v.id)])];
        onNodeUpdate(node, {
          ...node.data,
          product_variant_ids: allIds,
          product_variant_data: allReg,
          allocable_product_variant_data: allAlloc
        });
      }
      return { ...p, [nodeId]: updatedNodeAlloc };
    });
  };

  const handleOptionChange = (nodeId, optId, checked, isRadio = false) => {
    const cur = selectedOptions[nodeId] || [];
    const sel = (isRadio || nodeOptions[nodeId]?.selectionType === 'radio')
      ? [optId]
      : checked ? [...cur, optId] : cur.filter(i => i !== optId);

    setSelectedOptions(p => ({ ...p, [nodeId]: sel }));

    // Prune unchecked option from ordered list
    if (!checked && !isRadio) {
      setOrderedOptions(p => ({
        ...p,
        [nodeId]: (p[nodeId] || []).filter(o => (o.id ?? o.value) !== optId),
      }));
    }

    const node = rfNodes.find(n => n.id === nodeId);
    if (node && onNodeUpdate) {
      const cfg = nodeOptions[nodeId] || { selectionType: 'radio', options: [] };
      const updatedOptions = cfg.options.map(o => ({ ...o, selected: sel.includes(o.id || o.value) }));
      const selectedObjs = cfg.options.filter(o => sel.includes(o.id || o.value));
      onNodeUpdate(node, {
        options_config: { ...cfg, options: updatedOptions },
        options: { ...cfg, options: updatedOptions },
        selected_options: selectedObjs,
      });
    }
  };

  // Called by InlineOrderList on every ▲/▼ click
  const handleOrderChange = (nodeId, newIds, newItems) => {
    setOrderedOptions(p => ({ ...p, [nodeId]: newItems }));
    setSelectedOptions(p => ({ ...p, [nodeId]: newIds }));

    const node = rfNodes.find(n => n.id === nodeId);
    if (node && onNodeUpdate) {
      const cfg = nodeOptions[nodeId] || { selectionType: 'checkbox', options: [] };
      const updatedOptions = cfg.options.map(o => ({ ...o, selected: newIds.includes(o.id || o.value) }));
      onNodeUpdate(node, {
        options_config: { ...cfg, options: updatedOptions },
        options: { ...cfg, options: updatedOptions },
        selected_options: newItems,
        selected_options_order: newIds,
      });
    }
  };

  const handleTransformationsChange = (nodeId, newTransformations) => {
    setTransformations(p => ({ ...p, [nodeId]: newTransformations }));
  };

  const getSelectedOptionObjects = (nodeId) => {
    const ordered = orderedOptions[nodeId] || [];
    const selIds = selectedOptions[nodeId] || [];
    const cfg = nodeOptions[nodeId] || { options: [] };
    console.log('Getting selected option objects for node', nodeId, { ordered, selIds, cfg });
    if (ordered.length === selIds.length && selIds.every(sel => ordered.some(o => String(o.id ?? o.value) === String(sel)))) return ordered;
    return cfg.options.filter(o => selIds.some(s => String(s) === String(o.id) || s === o.value));
  };

  const getCombinedVariantIds = (nodeId) => {
    const reg = Object.values(selectedVariants[nodeId] || {}).flat().filter(v => v?.id).map(v => v.id);
    const alc = Object.values(selectedAllocableVariants[nodeId] || {}).flat().filter(v => v?.id).map(v => v.id);
    return [...new Set([...reg, ...alc])];
  };

  const saveNode = async (node) => {
    const nodeId = node.id, backendId = node.data?.backendId, nodeData = fetchedNodeData[nodeId];
    if (!backendId || !nodeData) return { success: false };
    try {
      const isOpt = node.type === 'option' || nodeData?.type === 'option' || nodeData?.options_node;
      const ids = getCombinedVariantIds(nodeId);
      const isExistingNode = Boolean(hasWorkflowVariantId);
      if (isExistingNode && ids.length > 0) {
        await patchFetcher(['/new/workflow-app/product/variant/add/in/output/', { node_id: backendId, output_id: nodeData?.output_foreign?.[0]?.id || '', product_variant_ids: ids }]);
        setAllVariantIds(p => [...new Set([...p, ...ids])]);
      }
      const fd = new FormData();
      fd.append('name', nodeData.name);
      if (nodeData.type) fd.append('type', nodeData.type);
      if (isOpt) {
        const opts = getSelectedOptionObjects(nodeId);
        const cfg = nodeOptions[nodeId] || nodeData?.options_node || { selectionType: 'radio', options: [] };
        const selOpts = (opts && opts.length > 0)
          ? opts
          : (nodeData?.selected_options || fetchedNodeData[nodeId]?.selected_options || []);

        if (selOpts.length > 0) {
          const selectedOptionsWithOrder = selOpts.map((opt, index) => ({
            option_id: opt.id || opt.value,
            id: opt.id || opt.value,
            value: opt.id || opt.value,
            label: opt.label,
            cost: opt.cost,
            order: index + 1,
            ...opt
          }));
          fd.append('option_config', JSON.stringify({ 
            selectionType: cfg.selectionType || 'radio', 
            options: (cfg.options || []).map(o => ({ 
              ...o, 
              selected: selOpts.some(s => s.id === o.id || s.value === o.value || String(s.id || s.value) === String(o.id || o.value)) 
            })) 
          }));
          fd.append('selected_options', JSON.stringify(selectedOptionsWithOrder));
        } else if (nodeData?.options_node) {
          fd.append('option_config', JSON.stringify(nodeData.options_node));
        }
      }
      fd.append('input_foreign_data', JSON.stringify({ name: nodeData.input_foreign?.[0]?.name || nodeData.name + ' Input', product_ids: nodeData.input_foreign?.[0]?.value?.product_ids || [], utility_ids: nodeData.input_foreign?.[0]?.value?.utility_ids || [], product_variant_ids: ids }));
      fd.append('output_foreign_ids', JSON.stringify(nodeData.output_foreign?.map(o => o.id) || []));

      // Include transformations in the output_foreign data
      const outputTransformations = transformations[nodeId] || [];
      if (outputTransformations.length > 0 && nodeData.output_foreign?.[0]) {
        const updatedOutputForeign = nodeData.output_foreign.map((out, idx) =>
          idx === 0 ? { ...out, transformations: outputTransformations } : out
        );
        fd.append('output_foreign_data', JSON.stringify(updatedOutputForeign));
      }

      fd.append('cost_foreign_ids', JSON.stringify(nodeData.cost_foreign?.map(c => c.id) || []));
      if (nodeData.machine) fd.append('machine_ids', JSON.stringify(nodeData.machine.map(m => m.id) || []));
      if (nodeData.users) fd.append('user_ids', JSON.stringify(nodeData.users));
      const cfg = { headers: { 'Content-Type': 'multipart/form-data' } };
      let res;
      if (isExistingNode) {
        try {
          res = await axiosInstance.patch(`/new/workflow-app/nodes/update/${backendId}/`, fd, cfg);
        } catch (patchErr) {
          console.log('PATCH node update failed, falling back to POST create:', patchErr);
          res = await axiosInstance.post('/new/workflow-app/nodes/create/', fd, cfg);
        }
      } else {
        res = await axiosInstance.post('/new/workflow-app/nodes/create/', fd, cfg);
      }

      const selectedVarObjs = Object.values(selectedVariants[nodeId] || {}).flat().filter(Boolean);
      const selectedAllocObjs = Object.values(selectedAllocableVariants[nodeId] || {}).flat().filter(Boolean);
      const optsObj = isOpt ? getSelectedOptionObjects(nodeId) : [];

      const createdId = res.data?.id || res.data?.backendId || res.data?.node_id || backendId;

      if (onNodeUpdate) {
        onNodeUpdate(node, {
          ...node.data,
          backendId: createdId,
          id: createdId ? createdId.toString() : node.id,
          updated: true,
          label: nodeData.name,
          product_variant_ids: ids,
          product_variant_data: selectedVarObjs,
          allocable_product_variant_data: selectedAllocObjs,
          ...(isOpt ? { selected_options: optsObj } : {})
        });
      }
      return { success: true, createdId, nodeId: node.id };
    } catch (err) {
      console.error(err);
      return { success: false, error: err.message };
    }
  };

  const handleSaveAll = async () => {
    setSavingAll(true);
    const nodeUpdates = {};
    try {
      const all = [...variantNodes, ...optionNodes, ...regularNodes];
      if (!all.length) { enqueueSnackbar('No nodes to save', { variant: 'info' }); return nodeUpdates; }
      enqueueSnackbar(`Saving ${all.length} nodes…`, { variant: 'info' });
      let ok = 0, fail = 0;
      for (const n of all) {
        const r = await saveNode(n);
        if (r.success) {
          ok++;
          if (r.createdId) {
            nodeUpdates[n.id] = r.createdId;
            if (n.data?.backendId) {
              nodeUpdates[n.data.backendId] = r.createdId;
            }
          }
        } else {
          fail++;
        }
      }
      if (fail === 0) { enqueueSnackbar(`All ${ok} nodes saved`, { variant: 'success' }); setissaveworkflowready(true); }
      else enqueueSnackbar(`Saved ${ok}, failed ${fail}`, { variant: 'warning' });
      return nodeUpdates;
    } catch {
      enqueueSnackbar('Save failed', { variant: 'error' });
      return nodeUpdates;
    }
    finally { setSavingAll(false); }
  };

  useEffect(() => {
    if (onRegisterSaveAll) {
      onRegisterSaveAll(handleSaveAll);
    }
    return () => {
      if (onRegisterSaveAll) {
        onRegisterSaveAll(null);
      }
    };
  }, [onRegisterSaveAll, handleSaveAll]);

  if (!open) return null;

  const totalNodes = rfNodes.length;
  const savedCount = rfNodes.filter(n => n.data?.updated).length;

  return (
    <Box sx={{ position: 'fixed', inset: 0, bgcolor: '#f1f5f9', zIndex: 1200, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

      {/* ── Header ── */}
      <Box sx={{
        flexShrink: 0, px: 3, py: 1.75,
        bgcolor: '#fff', borderBottom: '1px solid #e2e8f0',
        display: 'flex', alignItems: 'center', gap: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mr: 1 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: T.variantAccent, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 2px 8px ${T.variantAccent}40` }}>
            <Iconify icon="mdi:view-dashboard-variant" width={18} sx={{ color: '#fff' }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: '15px', color: '#0f172a', letterSpacing: '-0.3px', lineHeight: 1.2 }}>Smart View</Typography>
            <Typography sx={{ fontSize: '11px', color: '#94a3b8' }}>{savedCount}/{totalNodes} nodes saved</Typography>
          </Box>
        </Box>

        <Box sx={{ flex: 1, maxWidth: 200, display: { xs: 'none', md: 'block' } }}>
          <Box sx={{ height: 6, borderRadius: '50px', bgcolor: '#e2e8f0', overflow: 'hidden' }}>
            <Box sx={{
              height: '100%', borderRadius: '50px',
              bgcolor: savedCount === totalNodes && totalNodes > 0 ? '#22c55e' : T.variantAccent,
              width: totalNodes > 0 ? `${(savedCount / totalNodes) * 100}%` : '0%',
              transition: 'width 0.4s ease',
            }} />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 0.75 }}>
          {[
            { count: optionNodes.length, label: 'Option', bg: T.optionTag, color: T.optionTagTxt, icon: 'mdi:format-list-checks' },
            { count: variantNodes.length, label: 'Variant', bg: T.variantTag, color: T.variantTagTxt, icon: 'mdi:package-variant' },
            { count: regularNodes.length, label: 'Regular', bg: T.regularTag, color: T.regularTagTxt, icon: 'mdi:cube-outline' },
          ].map(({ count, label, bg, color, icon }) => (
            <Chip key={label}
              icon={<Iconify icon={icon} width={11} sx={{ color: `${color} !important`, ml: '6px !important' }} />}
              label={`${count} ${label}`} size="small"
              sx={{ height: 24, fontSize: '11px', fontWeight: 600, bgcolor: bg, color, borderRadius: '7px' }}
            />
          ))}
        </Box>

        <Box sx={{ display: 'flex', gap: 1, ml: 'auto', flexShrink: 0 }}>
          {finalQuantity?.product_ids?.length > 0 && (
            <Button variant="outlined" size="small" onClick={() => setShowVariantDialog(true)}
              startIcon={<Iconify icon="mdi:cube-outline" width={14} />}
              sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, fontSize: '12px' }}>
              Configure Variants
            </Button>
          )}
          <Button variant="contained" size="small" onClick={() => setShowSaveDialog(true)}
            disabled={savingAll || rfNodes.length === 0}
            startIcon={savingAll ? <CircularProgress size={13} sx={{ color: '#fff' }} /> : <Iconify icon="mdi:check-all" width={14} />}
            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, fontSize: '12px', bgcolor: '#16a34a', boxShadow: 'none', '&:hover': { bgcolor: '#15803d', boxShadow: 'none' }, '&.Mui-disabled': { bgcolor: '#e2e8f0', color: '#94a3b8' } }}>
            {savingAll ? 'Saving…' : 'Save Workflow'}
          </Button>
          <Button variant="outlined" size="small" onClick={onClose}
            startIcon={<Iconify icon="mdi:close" width={14} />}
            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, fontSize: '12px' }}>
            Close
          </Button>
        </Box>
      </Box>

      {/* ── Card grid ── */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 2.5 }}>
        {rfNodes.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 1.5 }}>
            <Box sx={{ width: 64, height: 64, borderRadius: '16px', bgcolor: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.5 }}>
              <Iconify icon="mdi:check-circle-outline" width={36} sx={{ color: '#22c55e' }} />
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: '16px', color: '#334155' }}>No nodes in workflow</Typography>
            <Typography sx={{ fontSize: '13px', color: '#94a3b8' }}>Add nodes to the canvas to get started</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 2, alignItems: 'stretch' }}>
            {rfNodes.map((node) => {
              const isOptionNode = node.type === 'option' || node.data?.type === 'option';
              const hasRawMaterial = node.data?.input?.some(i => i.is_material) || node.data?.hasRawMaterial;
              return (
                <NodeCard
                  key={node.id}
                  node={node}
                  isLoading={!!loading[node.id]}
                  nodeHasData={fetchedNodeData[node.id]}
                  isOptionNode={isOptionNode}
                  hasRawMaterial={hasRawMaterial}
                  nodeOptions={nodeOptions}
                  selectedOptions={selectedOptions}
                  onOptionChange={handleOptionChange}
                  variantOptions={variantOptions}
                  allocableVariantOptions={allocableVariantOptions}
                  selectedVariants={selectedVariants}
                  selectedAllocableVariants={selectedAllocableVariants}
                  onVariantChange={handleVariantChange}
                  onAllocableVariantChange={handleAllocableVariantChange}
                  nodeStates={nodeStates}
                  getCombinedVariantIds={getCombinedVariantIds}
                  onAllocableVariantAdded={handleAllocableVariantAdded}
                  orderedOptions={orderedOptions}
                  onOrderChange={handleOrderChange}
                  transformations={transformations}
                  onTransformationsChange={handleTransformationsChange}
                />
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
};

SmartView.propTypes = {
  nodes: PropTypes.array,
  rfNodes: PropTypes.array,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onNodeUpdate: PropTypes.func,
  allVariantIds: PropTypes.array,
  setAllVariantIds: PropTypes.func,
  setissaveworkflowready: PropTypes.func,
  setShowVariantDialog: PropTypes.func,
  finalQuantity: PropTypes.object,
  issaveworkflowready: PropTypes.bool,
  setShowSaveDialog: PropTypes.func,
};

export default SmartView;